import React, { useState, useEffect, useRef } from 'react';

const RECENT_LINE_COLOURS_KEY = 'point_atlas_recent_line_colours';
const RECENT_FILL_COLOURS_KEY = 'point_atlas_recent_fill_colours';

function getRecentColours(key) {
  try {
    const arr = JSON.parse(localStorage.getItem(key) || '[]');
    if (Array.isArray(arr)) return arr;
  } catch (e) {}
  return [];
}
function addRecentColour(key, hex) {
  try {
    let arr = getRecentColours(key);
    arr = arr.filter(x => x !== hex);
    arr.unshift(hex);
    arr = arr.slice(0, 5);
    localStorage.setItem(key, JSON.stringify(arr));
  } catch (e) {}
}

const toolbarStyle = {
  position: 'absolute',
  top: 16,
  left: 70,
  display: 'flex',
  alignItems: 'center',
  background: '#191a1f',
  borderRadius: '12px',
  boxShadow: '0 2px 12px #000b',
  padding: '6px 14px 6px 14px',
  zIndex: 2500,
  gap: 12,
  height: 40,
  minWidth: 420,
};

function ColourPickerModal({
  value,
  recent,
  onChange,
  onCancel,
  onConfirm,
  title = "Pick Colour"
}) {
  const [internalValue, setInternalValue] = useState(value);
  const originalValue = useRef(value);

  useEffect(() => {
    setInternalValue(value);
    originalValue.current = value;
  }, [value]);

  function handleCancel() {
    setInternalValue(originalValue.current);
    onChange(originalValue.current);
    onCancel();
  }

  function handleConfirm() {
    onConfirm(internalValue);
  }

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(20,20,22,0.45)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        background: '#23242a',
        padding: 32,
        borderRadius: 13,
        boxShadow: '0 2px 20px #000a',
        minWidth: 320,
        display: 'flex', flexDirection: 'column', gap: 18,
        alignItems: 'stretch'
      }}>
        <div style={{ color: '#ffe670', fontWeight: 700, fontSize: 19 }}>
          {title}
        </div>
        <input
          type="color"
          value={internalValue}
          onChange={e => {
            setInternalValue(e.target.value);
            onChange(e.target.value);
          }}
          style={{
            width: 90,
            height: 48,
            border: 'none',
            borderRadius: 12,
            alignSelf: 'center',
            background: 'none',
            cursor: 'pointer',
            boxShadow: '0 0 0 2px #444'
          }}
        />
        {recent.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', marginTop: 6 }}>
            {recent.map(hex => (
              <span
                key={hex}
                onClick={() => {
                  setInternalValue(hex);
                  onChange(hex);
                }}
                style={{
                  width: 28,
                  height: 28,
                  background: hex,
                  borderRadius: 8,
                  border: hex === internalValue ? '3px solid #ffe670' : '1px solid #444',
                  display: 'inline-block',
                  cursor: 'pointer',
                  boxShadow: hex === '#fff' ? '0 0 0 1px #666' : ''
                }}
                title={hex}
              />
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 14, marginTop: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={handleCancel}
            style={{
              background: '#31313c',
              color: '#eee',
              border: 'none',
              borderRadius: 7,
              padding: '8px 23px',
              fontWeight: 700,
              fontSize: 15,
              cursor: 'pointer'
            }}
          >Cancel</button>
          <button
            onClick={handleConfirm}
            style={{
              background: '#ffe670',
              color: '#23242a',
              border: 'none',
              borderRadius: 7,
              padding: '8px 23px',
              fontWeight: 700,
              fontSize: 15,
              cursor: 'pointer'
            }}
          >Confirm</button>
        </div>
      </div>
    </div>
  );
}

function toSnakeCaseShape(shape) {
  if (!shape) return shape;
  return {
    id: shape.id,
    map_id: shape.mapId || shape.map_id,
    label: shape.label,
    geometry: shape.geometry,
    fill_color: shape.fillColor,
    fill_opacity: shape.fillOpacity,
    stroke_color: shape.strokeColor,
    stroke_opacity: shape.strokeOpacity,
    radius: shape.radius,
    pattern: shape.pattern,
    shape_type: shape.shapeType,
  };
}

export default function ShapeEditToolbar({
  shape,
  layer,
  onChange,
  onSave,
  onClose,
  onDelete,
  saving = false,
  canEdit = false,
  convertToSnakeCase = false,
}) {
  const [labelModalOpen, setLabelModalOpen] = useState(false);
  const [tempLabel, setTempLabel] = useState(shape.label || '');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editing, setEditing] = useState(false);

  const [colourModal, setColourModal] = useState(null);
  const [recentLineColours, setRecentLineColours] = useState(getRecentColours(RECENT_LINE_COLOURS_KEY));
  const [recentFillColours, setRecentFillColours] = useState(getRecentColours(RECENT_FILL_COLOURS_KEY));

  useEffect(() => {
    setRecentLineColours(getRecentColours(RECENT_LINE_COLOURS_KEY));
    setRecentFillColours(getRecentColours(RECENT_FILL_COLOURS_KEY));
  }, []);

  useEffect(() => {
    if (!layer) return;
    if (layer.setStyle) {
      let styleObj = {
        color: shape.strokeColor || '#3388ff',
        fillColor: shape.fillColor || '#3388ff',
        opacity: shape.strokeOpacity ?? 1,
        fillOpacity: shape.fillOpacity ?? 0.5,
      };
      layer.setStyle(styleObj);
    }
    if (layer instanceof window.L.Circle) {
      layer.setStyle({
        color: shape.strokeColor || '#3388ff',
        fillColor: shape.fillColor || '#3388ff',
        opacity: shape.strokeOpacity ?? 1,
        fillOpacity: shape.fillOpacity ?? 0.5,
      });
      if (shape.radius) layer.setRadius(shape.radius);
    }
    if (layer && layer._popup && layer._popup.isOpen()) {
      layer._popup.setContent(shape.label || "");
    }
  }, [
    shape.strokeColor,
    shape.fillColor,
    shape.strokeOpacity,
    shape.fillOpacity,
    shape.radius,
    shape.label,
    layer,
  ]);

  useEffect(() => {
    if (!layer || !layer.pm) return;
    if (canEdit && editing) {
      if (!layer.pm.enabled()) {
        layer.pm.enable({ allowSelfIntersection: false });
      }
    } else {
      if (layer.pm && layer.pm.enabled()) {
        layer.pm.disable();
      }
    }
    return () => {
      if (layer && layer.pm && layer.pm.enabled()) {
        layer.pm.disable();
      }
    };
  }, [editing, layer, canEdit]);

  if (!shape || !canEdit) return null;

  function callOnChange(shapeUpdate) {
    if (convertToSnakeCase) {
      onChange(toSnakeCaseShape(shapeUpdate));
    } else {
      onChange(shapeUpdate);
    }
  }

  function openLineColourModal() {
    setColourModal({
      type: 'line',
      original: shape.strokeColor || '#3388ff',
      value: shape.strokeColor || '#3388ff'
    });
  }
  function openFillColourModal() {
    setColourModal({
      type: 'fill',
      original: shape.fillColor || '#3388ff',
      value: shape.fillColor || '#3388ff'
    });
  }

  function handleColourChange(hex) {
    if (!colourModal) return;
    if (colourModal.type === 'line') {
      callOnChange({ ...shape, strokeColor: hex });
    } else {
      callOnChange({ ...shape, fillColor: hex });
    }
  }

  function handleColourCancel() {
    if (!colourModal) return;
    if (colourModal.type === 'line') {
      callOnChange({ ...shape, strokeColor: colourModal.original });
    } else {
      callOnChange({ ...shape, fillColor: colourModal.original });
    }
    setColourModal(null);
  }

  function handleColourConfirm(hex) {
    if (!colourModal) return;
    if (colourModal.type === 'line') {
      addRecentColour(RECENT_LINE_COLOURS_KEY, hex);
      setRecentLineColours(getRecentColours(RECENT_LINE_COLOURS_KEY));
      callOnChange({ ...shape, strokeColor: hex });
    } else {
      addRecentColour(RECENT_FILL_COLOURS_KEY, hex);
      setRecentFillColours(getRecentColours(RECENT_FILL_COLOURS_KEY));
      callOnChange({ ...shape, fillColor: hex });
    }
    setColourModal(null);
  }

  return (
    <div style={toolbarStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ color: '#fff', fontSize: 13 }}>Line</span>
        <span
          onClick={openLineColourModal}
          style={{
            display: 'inline-block',
            width: 26,
            height: 26,
            borderRadius: 7,
            background: shape.strokeColor || '#3388ff',
            border: '1.5px solid #ffe670',
            cursor: 'pointer',
            boxShadow: '0 1px 6px #000b'
          }}
          title="Line Colour"
        />
        <input
          type="range"
          min="0.05"
          max="1"
          step="0.01"
          value={shape.strokeOpacity ?? 1}
          onChange={e => callOnChange({ ...shape, strokeOpacity: parseFloat(e.target.value) })}
          style={{ width: 60, marginLeft: 5 }}
          title="Line Opacity"
        />
        <span style={{ color: '#ffe670', fontSize: 13, marginLeft: 4 }}>
          {Math.round((shape.strokeOpacity ?? 1) * 100)}%
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ color: '#fff', fontSize: 13 }}>Fill</span>
        <span
          onClick={openFillColourModal}
          style={{
            display: 'inline-block',
            width: 26,
            height: 26,
            borderRadius: 7,
            background: shape.fillColor || '#3388ff',
            border: '1.5px solid #ffe670',
            cursor: 'pointer',
            boxShadow: '0 1px 6px #000b'
          }}
          title="Fill Colour"
        />
        <input
          type="range"
          min="0.05"
          max="1"
          step="0.01"
          value={shape.fillOpacity ?? 0.5}
          onChange={e => callOnChange({ ...shape, fillOpacity: parseFloat(e.target.value) })}
          style={{ width: 60, marginLeft: 5 }}
          title="Fill Opacity"
        />
        <span style={{ color: '#ffe670', fontSize: 13, marginLeft: 4 }}>
          {Math.round((shape.fillOpacity ?? 0.5) * 100)}%
        </span>
      </div>
      <span
        style={{
          background: '#24253a',
          color: '#fff',
          border: '1px solid #393b49',
          borderRadius: 7,
          padding: '5px 13px',
          fontWeight: 600,
          fontSize: 14,
          marginRight: 3,
        }}
      >
        {shape.label ? shape.label : "Zone"}
      </span>
      <button
        onClick={() => setLabelModalOpen(true)}
        style={{
          background: '#24253a',
          color: '#fff',
          border: '1px solid #393b49',
          borderRadius: 7,
          padding: '5px 13px',
          fontWeight: 600,
          fontSize: 14,
          cursor: 'pointer',
          marginRight: 3,
        }}
        title="Edit Zone Name"
      >
        Edit Name
      </button>
      {/* Edit button hidden temporarily
      <button
        onClick={() => setEditing(e => !e)}
        style={{
          background: editing ? '#ffe670' : '#393b49',
          color: editing ? '#23242a' : '#fff',
          border: 'none',
          borderRadius: 7,
          padding: '6px 17px',
          fontWeight: 700,
          fontSize: 16,
          marginLeft: 10,
          cursor: 'pointer',
        }}
        title={editing ? "Finish Editing Shape" : "Edit Shape"}
      >
        {editing ? "Finish" : "Edit"}
      </button>
      */}
      <button
        onClick={onSave}
        disabled={saving}
        style={{
          background: '#4cc153',
          color: '#fff',
          border: 'none',
          borderRadius: 7,
          padding: '6px 18px',
          fontWeight: 700,
          fontSize: 16,
          cursor: saving ? 'not-allowed' : 'pointer',
          marginLeft: 10,
        }}
      >
        {saving ? "Saving..." : "Save"}
      </button>
      <button
        onClick={() => setShowDeleteModal(true)}
        style={{
          background: '#e04c53',
          color: '#fff',
          border: 'none',
          borderRadius: 7,
          padding: '6px 18px',
          fontWeight: 700,
          fontSize: 16,
          marginLeft: 8,
          cursor: 'pointer',
        }}
        title="Delete"
      >Delete</button>
      <button
        onClick={onClose}
        style={{
          background: 'none',
          color: '#fff',
          border: 'none',
          fontSize: 20,
          marginLeft: 2,
          cursor: 'pointer',
          padding: 2,
          borderRadius: 6,
          lineHeight: 1,
        }}
        title="Close"
      >×</button>
      {labelModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(20,20,22,0.45)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#23242a',
            padding: 28,
            borderRadius: 13,
            boxShadow: '0 2px 20px #000a',
            minWidth: 300,
            display: 'flex', flexDirection: 'column', gap: 12,
            alignItems: 'stretch'
          }}>
            <div style={{ color: '#ffe670', fontWeight: 700, fontSize: 19, marginBottom: 9 }}>
              Edit Zone Name
            </div>
            <input
              type="text"
              value={tempLabel}
              onChange={e => setTempLabel(e.target.value)}
              placeholder="Zone Name"
              style={{
                fontSize: 17,
                padding: '7px 12px',
                borderRadius: 7,
                border: '1px solid #393b49',
                outline: 'none',
                background: '#16171b',
                color: '#ffe670',
                fontWeight: 600
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
              <button
                onClick={() => setLabelModalOpen(false)}
                style={{
                  background: '#31313c',
                  color: '#eee',
                  border: 'none',
                  borderRadius: 7,
                  padding: '6px 17px',
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: 'pointer'
                }}
              >Cancel</button>
              <button
                onClick={() => {
                  setLabelModalOpen(false);
                  callOnChange({ ...shape, label: tempLabel });
                }}
                style={{
                  background: '#ffe670',
                  color: '#23242a',
                  border: 'none',
                  borderRadius: 7,
                  padding: '6px 17px',
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: 'pointer'
                }}
              >Set Name</button>
            </div>
          </div>
        </div>
      )}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(24, 16, 20, 0.66)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#222124',
            padding: 38,
            borderRadius: 13,
            boxShadow: '0 2px 30px #000a',
            minWidth: 350,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 24
          }}>
            <div style={{
              color: '#ffe670',
              fontWeight: 700,
              fontSize: 21,
              marginBottom: 8,
              textAlign: 'center'
            }}>
              Are you sure you want to delete this zone?
            </div>
            <div style={{
              display: 'flex',
              gap: 14,
              marginTop: 6
            }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  background: '#31313c',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 7,
                  padding: '8px 23px',
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: 'pointer'
                }}
              >Cancel</button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  if (onDelete) onDelete(convertToSnakeCase ? toSnakeCaseShape(shape) : shape, layer);
                }}
                style={{
                  background: '#e04c53',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 7,
                  padding: '8px 23px',
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: 'pointer'
                }}
              >Delete</button>
            </div>
          </div>
        </div>
      )}
      {colourModal && (
        <ColourPickerModal
          value={colourModal.type === 'line' ? shape.strokeColor || '#3388ff' : shape.fillColor || '#3388ff'}
          recent={colourModal.type === 'line' ? recentLineColours : recentFillColours}
          onChange={handleColourChange}
          onCancel={handleColourCancel}
          onConfirm={handleColourConfirm}
          title={colourModal.type === 'line' ? 'Pick Line Colour' : 'Pick Fill Colour'}
        />
      )}
    </div>
  );
}
